class ErrorTrackingService {
  constructor() {
    this.sessionId = this.generateSessionId();
    this.errorQueue = [];
    this.maxQueueSize = 50;
    this.isProcessing = false;
  }

  generateSessionId() {
    return `session_${Date.now()}_${Math.random()?.toString(36)?.substr(2, 9)}`;
  }

  captureError(error, context = {}) {
    // CRITICAL: Wrap entire function in try-catch to prevent crashes
    try {
      const errorData = {
        sessionId: this.sessionId,
        timestamp: new Date()?.toISOString(),
        errorType: error?.name || 'UnknownError',
        message: error?.message || String(error),
        stack: error?.stack || new Error()?.stack,
        url: typeof window !== 'undefined' ? window?.location?.href : 'unknown',
        userAgent: typeof navigator !== 'undefined' ? navigator?.userAgent : 'unknown',
        viewport: typeof window !== 'undefined' ? {
          width: window?.innerWidth,
          height: window?.innerHeight
        } : null,
        context: {
          ...context,
          componentStack: error?.componentStack,
          // SAFE: Use optional chaining and never assume __ErrorBoundary exists
          errorBoundary: error?.__ErrorBoundary || false
        },
        userContext: this.getUserContext(),
        severity: this.determineSeverity(error, context)
      };

      // Log to console for development (with safe checks)
      if (typeof console !== 'undefined' && console?.error) {
        console.error('🔴 Error Captured:', {
          type: errorData?.errorType,
          message: errorData?.message,
          context: errorData?.context,
          severity: errorData?.severity
        });
      }

      // Add to queue (with safe array check)
      if (Array.isArray(this.errorQueue)) {
        this.errorQueue?.push(errorData);
        
        // Trim queue if too large
        if (this.errorQueue?.length > this.maxQueueSize) {
          this.errorQueue?.shift();
        }
      }

      // Process queue
      this.processQueue();

      return errorData;
    } catch (captureError) {
      // CRITICAL: captureError must NEVER crash
      if (typeof console !== 'undefined' && console?.error) {
        console.error('⚠️ Error tracking failed (non-fatal):', captureError?.message);
      }
      return null;
    }
  }

  captureEventHandlerError(eventType, handler, error, element) {
    try {
      return this.captureError(error, {
        errorCategory: 'EventHandler',
        eventType,
        handlerName: handler?.name || 'anonymous',
        elementTag: element?.tagName,
        elementId: element?.id,
        elementClass: element?.className
      });
    } catch (e) {
      if (typeof console !== 'undefined' && console?.error) {
        console.error('⚠️ captureEventHandlerError failed (non-fatal):', e?.message);
      }
      return null;
    }
  }

  captureStateError(stateType, operation, error, previousState, newState) {
    try {
      return this.captureError(error, {
        errorCategory: 'StateManagement',
        stateType,
        operation,
        previousState: this.sanitizeState(previousState),
        newState: this.sanitizeState(newState)
      });
    } catch (e) {
      if (typeof console !== 'undefined' && console?.error) {
        console.error('⚠️ captureStateError failed (non-fatal):', e?.message);
      }
      return null;
    }
  }

  captureApiError(endpoint, method, error, requestData, responseData) {
    try {
      return this.captureError(error, {
        errorCategory: 'API',
        endpoint,
        method,
        requestData: this.sanitizeData(requestData),
        responseData: this.sanitizeData(responseData),
        statusCode: error?.status || error?.statusCode
      });
    } catch (e) {
      if (typeof console !== 'undefined' && console?.error) {
        console.error('⚠️ captureApiError failed (non-fatal):', e?.message);
      }
      return null;
    }
  }

  capturePromiseRejection(reason, promise) {
    try {
      const error = reason instanceof Error ? reason : new Error(String(reason));
      return this.captureError(error, {
        errorCategory: 'UnhandledPromiseRejection',
        promiseState: 'rejected'
      });
    } catch (e) {
      if (typeof console !== 'undefined' && console?.error) {
        console.error('⚠️ capturePromiseRejection failed (non-fatal):', e?.message);
      }
      return null;
    }
  }

  getUserContext() {
    try {
      // SAFE: Check if supabase and required methods exist
      // Remove undeclared 'supabase' variable - set user to null directly
      const user = null; // Async, skip for now
      
      // SAFE: Check localStorage exists and is accessible
      let authenticated = false;
      try {
        authenticated = typeof localStorage !== 'undefined' && !!localStorage.getItem('supabase.auth.token');
      } catch (storageError) {
        // localStorage may not be accessible (privacy mode, etc.)
        authenticated = false;
      }
      
      return {
        authenticated,
        sessionStorage: this.getRelevantStorageData(),
        currentRoute: typeof window !== 'undefined' ? window?.location?.pathname : 'unknown',
        referrer: typeof document !== 'undefined' ? document?.referrer : 'unknown'
      };
    } catch (e) {
      if (typeof console !== 'undefined' && console?.warn) {
        console.warn('⚠️ getUserContext failed (non-fatal):', e?.message);
      }
      return { error: 'Failed to get user context' };
    }
  }

  getRelevantStorageData() {
    try {
      // SAFE: Check sessionStorage exists and is accessible
      if (typeof sessionStorage === 'undefined') {
        return {};
      }
      
      const relevantKeys = ['lastVisitedProperty', 'userPreferences', 'sessionStartTime'];
      const data = {};
      
      relevantKeys?.forEach(key => {
        try {
          const value = sessionStorage.getItem(key);
          if (value) data[key] = value;
        } catch (itemError) {
          // Individual item access may fail, continue with others
        }
      });
      
      return data;
    } catch (e) {
      if (typeof console !== 'undefined' && console?.warn) {
        console.warn('⚠️ getRelevantStorageData failed (non-fatal):', e?.message);
      }
      return {};
    }
  }

  determineSeverity(error, context) {
    try {
      // Critical: Auth errors, payment errors, data loss
      if (error?.message?.toLowerCase()?.includes('auth') ||
          error?.message?.toLowerCase()?.includes('payment') ||
          error?.message?.toLowerCase()?.includes('stripe') ||
          context?.errorCategory === 'StateManagement') {
        return 'critical';
      }

      // High: API errors, event handler failures
      if (context?.errorCategory === 'API' ||
          context?.errorCategory === 'EventHandler') {
        return 'high';
      }

      // Medium: UI errors, validation errors
      if (error?.name === 'ValidationError' ||
          context?.errorBoundary) {
        return 'medium';
      }

      // Low: Everything else
      return 'low';
    } catch (e) {
      // If severity determination fails, default to 'medium'
      return 'medium';
    }
  }

  sanitizeState(state) {
    if (!state) return null;
    try {
      const sanitized = JSON.parse(JSON.stringify(state));
      // Remove sensitive data
      if (sanitized?.password) delete sanitized?.password;
      if (sanitized?.token) delete sanitized?.token;
      if (sanitized?.apiKey) delete sanitized?.apiKey;
      return sanitized;
    } catch (e) {
      if (typeof console !== 'undefined' && console?.warn) {
        console.warn('⚠️ sanitizeState failed (non-fatal):', e?.message);
      }
      return { error: 'Failed to sanitize state' };
    }
  }

  sanitizeData(data) {
    if (!data) return null;
    try {
      const sanitized = JSON.parse(JSON.stringify(data));
      // Remove sensitive fields
      const sensitiveFields = ['password', 'token', 'apiKey', 'creditCard', 'ssn'];
      const removeSensitive = (obj) => {
        if (typeof obj !== 'object' || obj === null) return;
        Object.keys(obj)?.forEach(key => {
          // SAFE: Guard against undefined key or field values in .some() callback
          try {
            if (sensitiveFields?.some(field => {
              // SAFE: Ensure both field and key exist before comparison
              if (!field || !key) return false;
              return key?.toLowerCase()?.includes(field);
            })) {
              obj[key] = '[REDACTED]';
            } else if (typeof obj?.[key] === 'object') {
              removeSensitive(obj?.[key]);
            }
          } catch (keyError) {
            // SAFE: If any key processing fails, skip it and continue
            if (typeof console !== 'undefined' && console?.warn) {
              console.warn('⚠️ Failed to process key (non-fatal):', key);
            }
          }
        });
      };
      removeSensitive(sanitized);
      return sanitized;
    } catch (e) {
      if (typeof console !== 'undefined' && console?.warn) {
        console.warn('⚠️ sanitizeData failed (non-fatal):', e?.message);
      }
      return { error: 'Failed to sanitize data' };
    }
  }

  async processQueue() {
    // Wrap entire function in try-catch
    try {
      if (this.isProcessing || !Array.isArray(this.errorQueue) || this.errorQueue?.length === 0) {
        return;
      }

      this.isProcessing = true;

      try {
        // Process in batches
        const batch = this.errorQueue?.splice(0, 10);
        
        // Log to console (in production, this would send to a logging service)
        if (typeof console !== 'undefined' && console?.group) {
          console.group('📊 Error Batch Processing');
          batch?.forEach((error, index) => {
            if (console?.log) {
              console.log(`Error ${index + 1}:`, {
                severity: error?.severity,
                type: error?.errorType,
                message: error?.message,
                category: error?.context?.errorCategory,
                timestamp: error?.timestamp
              });
            }
          });
          if (console?.groupEnd) {
            console.groupEnd();
          }
        }

        // In production, you would send to a service like Sentry, LogRocket, etc.
        // await this.sendToLoggingService(batch);

      } catch (batchError) {
        if (typeof console !== 'undefined' && console?.error) {
          console.error('Failed to process error batch (non-fatal):', batchError?.message);
        }
      } finally {
        this.isProcessing = false;
        
        // Process remaining errors
        if (Array.isArray(this.errorQueue) && this.errorQueue?.length > 0) {
          setTimeout(() => this.processQueue(), 1000);
        }
      }
    } catch (queueError) {
      // CRITICAL: processQueue must never crash
      if (typeof console !== 'undefined' && console?.error) {
        console.error('⚠️ Error queue processing failed (non-fatal):', queueError?.message);
      }
      this.isProcessing = false;
    }
  }

  getErrorStats() {
    try {
      // SAFE: Check errorQueue exists and is an array
      if (!Array.isArray(this.errorQueue)) {
        return {
          totalErrors: 0,
          bySeverity: {},
          byCategory: {},
          byType: {}
        };
      }
      
      const stats = {
        totalErrors: this.errorQueue?.length,
        bySeverity: {},
        byCategory: {},
        byType: {}
      };

      // SAFE: Use optional chaining and check each error exists
      this.errorQueue?.forEach(error => {
        if (!error) return;
        
        // Count by severity
        const severity = error?.severity || 'unknown';
        stats.bySeverity[severity] = (stats?.bySeverity?.[severity] || 0) + 1;

        // Count by category
        const category = error?.context?.errorCategory || 'uncategorized';
        stats.byCategory[category] = (stats?.byCategory?.[category] || 0) + 1;

        // Count by type
        const type = error?.errorType || 'unknown';
        stats.byType[type] = (stats?.byType?.[type] || 0) + 1;
      });

      return stats;
    } catch (e) {
      if (typeof console !== 'undefined' && console?.error) {
        console.error('⚠️ getErrorStats failed (non-fatal):', e?.message);
      }
      return {
        totalErrors: 0,
        bySeverity: {},
        byCategory: {},
        byType: {},
        error: 'Failed to compute stats'
      };
    }
  }

  clearErrors() {
    this.errorQueue = [];
  }

  getRecentErrors(count = 10) {
    return this.errorQueue?.slice(-count);
  }
}

// Create singleton instance
const errorTrackingService = new ErrorTrackingService();

// Setup global error handlers
if (typeof window !== 'undefined') {
  // Capture unhandled errors
  window.addEventListener('error', (event) => {
    errorTrackingService?.captureError(event?.error || new Error(event.message), {
      errorCategory: 'UnhandledError',
      filename: event?.filename,
      lineno: event?.lineno,
      colno: event?.colno
    });
  });

  // Capture unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    errorTrackingService?.capturePromiseRejection(event?.reason, event?.promise);
  });

  // Expose for ErrorBoundary integration
  window.__COMPONENT_ERROR__ = (error, errorInfo) => {
    errorTrackingService?.captureError(error, {
      errorCategory: 'ReactErrorBoundary',
      componentStack: errorInfo?.componentStack
    });
  };

  // Expose for debugging
  window.__ERROR_TRACKING__ = {
    getStats: () => errorTrackingService?.getErrorStats(),
    getRecent: (count) => errorTrackingService?.getRecentErrors(count),
    clear: () => errorTrackingService?.clearErrors()
  };
}

export default errorTrackingService;
